import React, { useEffect, useState, useCallback } from "react";
import { api, API, errMsg, fmtMoney, fmtNum, fmtDate, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Fuel, LogOut, LayoutDashboard, ShoppingCart, Wallet, Truck, User,
  Loader2, Upload, FileText, CheckCircle2, Clock,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "order", label: "Place Order", icon: ShoppingCart },
  { key: "orders", label: "My Orders", icon: FileText },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "deliveries", label: "Delivery Tracking", icon: Truck },
  { key: "profile", label: "Profile", icon: User },
];

function Card({ children, className = "", ...rest }) {
  return <div {...rest} className={`bg-white border border-slate-200 rounded-lg ${className}`}>{children}</div>;
}

function Overview({ summary, go }) {
  if (!summary) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  const c = summary.customer || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { label: "Open Orders", value: fmtNum(summary.open_orders), tone: "text-[#12346B]" },
          { label: "Total Orders", value: fmtNum(summary.orders_count), tone: "text-slate-800" },
          { label: "Outstanding Balance", value: fmtMoney(summary.outstanding), tone: "text-orange-600" },
          { label: "Credit Limit", value: fmtMoney(summary.credit_limit), tone: summary.credit_limit > 0 ? "text-emerald-600" : "text-slate-500" },
        ].map((k) => (
          <Card key={k.label} className="p-4" data-testid={`portal-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className={`font-head text-2xl font-bold mt-1 tabular ${k.tone}`}>{k.value}</div>
          </Card>
        ))}
      </div>
      <Card className="p-6">
        <h3 className="font-head font-semibold text-slate-800 mb-1">Welcome, {c.name}</h3>
        <p className="text-sm text-slate-500">Payment terms: <b>{c.payment_terms}</b> · Account: <StatusBadge status={c.status} /></p>
        <div className="mt-4 flex gap-2">
          <Button onClick={() => go("order")} className="bg-[#12346B] hover:bg-[#12346B]/90" data-testid="portal-cta-order">
            <ShoppingCart className="h-4 w-4 mr-1.5" /> Place a New Order
          </Button>
          <Button variant="outline" onClick={() => go("orders")}>View My Orders</Button>
        </div>
      </Card>
    </div>
  );
}

function PlaceOrder({ reload, go }) {
  const [products, setProducts] = useState([]);
  const [pid, setPid] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/portal/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);
  const sel = products.find((p) => p.id === pid);
  const total = sel && qty ? sel.sell_price * Number(qty) : 0;

  const submit = async () => {
    if (!pid || !qty) { toast.error("Select a product and quantity"); return; }
    setBusy(true);
    try {
      await api.post("/portal/orders", { product_id: pid, quantity: Number(qty) });
      toast.success("Purchase order submitted for approval");
      reload(); go("orders");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 max-w-xl">
      <h3 className="font-head font-semibold text-slate-800 mb-4">Submit Purchase Order</h3>
      <div className="space-y-4">
        <div className="grid gap-1.5">
          <Label className="text-xs font-medium text-slate-600">Product</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="portal-order-product"><SelectValue placeholder="Select petroleum product…" /></SelectTrigger>
            <SelectContent className="bg-white z-[60]">
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} — {fmtMoney(p.sell_price)}/gal · {fmtNum(p.available)} gal available</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs font-medium text-slate-600">Quantity (Gallons)</Label>
          <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="portal-order-qty" placeholder="e.g. 100000" />
          {sel && <p className="text-xs text-slate-400">Max available: {fmtNum(sel.available)} gal</p>}
        </div>
        <div className="flex items-center justify-between rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
          <span className="text-sm text-slate-500">Estimated Total</span>
          <span className="font-head text-xl font-bold text-[#12346B] tabular">{fmtMoney(total)}</span>
        </div>
        <Button onClick={submit} disabled={busy} className="w-full bg-[#12346B] hover:bg-[#12346B]/90" data-testid="portal-order-submit">
          {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Submit Order for Approval
        </Button>
        <p className="text-xs text-slate-400 text-center">Orders are reviewed and approved by PCI before a Delivery Order is issued.</p>
      </div>
    </Card>
  );
}

function MyOrders({ go }) {
  const [orders, setOrders] = useState(null);
  const [uploading, setUploading] = useState(null);
  const load = useCallback(() => api.get("/portal/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([])), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (order, file) => {
    if (!file) return;
    setUploading(order.id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/portal/orders/${order.id}/payment-proof`, fd);
      toast.success("Payment proof uploaded — awaiting finance verification");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(null); }
  };

  if (!orders) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  if (!orders.length) return (
    <Card className="py-16 text-center text-slate-400 text-sm">
      No orders yet. <button className="text-[#12346B] underline" onClick={() => go("order")}>Place your first order</button>.
    </Card>
  );
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="pf-table">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Order", "Product", "Qty (L)", "Total", "Paid", "Status", "Payment Proof"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} className="border-slate-100" data-testid={`portal-order-${o.id}`}>
                <TableCell className="py-2 px-3 mono text-xs font-semibold">{o.so_no}</TableCell>
                <TableCell className="py-2 px-3 text-[13px]">{o.product_name}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtNum(o.quantity)}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtMoney(o.total_value)}</TableCell>
                <TableCell className="py-2 px-3">{o.paid ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Clock className="h-4 w-4 text-orange-500" />}</TableCell>
                <TableCell className="py-2 px-3"><StatusBadge status={o.status} /></TableCell>
                <TableCell className="py-2 px-3">
                  {o.paid ? <span className="text-xs text-emerald-600">Verified</span> : (
                    <label className="inline-flex items-center gap-1.5 text-xs text-[#12346B] cursor-pointer hover:underline" data-testid={`portal-upload-${o.id}`}>
                      {uploading === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload proof
                      <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => upload(o, e.target.files[0])} />
                    </label>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function Payments() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/portal/payments").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  if (!rows.length) return <Card className="py-16 text-center text-slate-400 text-sm">No payments recorded yet.</Card>;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="pf-table">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Receipt", "Order", "Amount", "Date", "Status", "Proof"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} className="border-slate-100" data-testid={`portal-payment-${p.id}`}>
                <TableCell className="py-2 px-3 mono text-xs font-semibold">{p.receipt_no}</TableCell>
                <TableCell className="py-2 px-3 mono text-xs">{p.so_no}</TableCell>
                <TableCell className="py-2 px-3 tabular">{fmtMoney(p.amount)}</TableCell>
                <TableCell className="py-2 px-3 text-xs text-slate-500">{fmtDay(p.payment_date)}</TableCell>
                <TableCell className="py-2 px-3"><StatusBadge status={p.status} /></TableCell>
                <TableCell className="py-2 px-3">
                  {p.proof_document_id ? (
                    <a href={`${API}/portal/documents/${p.proof_document_id}`} target="_blank" rel="noreferrer"
                      className="text-xs text-[#12346B] hover:underline inline-flex items-center gap-1" data-testid={`portal-proof-${p.id}`}>
                      <FileText className="h-3.5 w-3.5" /> View
                    </a>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function Deliveries() {
  const [dos, setDos] = useState(null);
  const [dels, setDels] = useState([]);
  useEffect(() => {
    api.get("/portal/delivery-orders").then(({ data }) => setDos(data)).catch(() => setDos([]));
    api.get("/portal/deliveries").then(({ data }) => setDels(data)).catch(() => {});
  }, []);
  if (!dos) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  if (!dos.length) return <Card className="py-16 text-center text-slate-400 text-sm">No delivery orders yet.</Card>;
  const delFor = (doNo) => dels.find((d) => d.do_no === doNo);
  return (
    <div className="space-y-3">
      {dos.map((d) => {
        const del = delFor(d.do_no);
        return (
          <Card key={d.id} className="p-4" data-testid={`portal-do-${d.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="mono text-sm font-semibold">{d.do_no}</span>
                  <StatusBadge status={d.status} />
                </div>
                <div className="text-sm text-slate-500 mt-0.5">{d.product_name} · {fmtNum(d.quantity)} gal · Vehicle {d.vehicle || "—"}</div>
              </div>
              <div className="text-right text-xs text-slate-400">
                <div>Issued {fmtDay(d.issue_date)}</div>
                <div className="mono">{d.qr_code}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs">
              {["Issued", "In Transit", "Delivered"].map((step, i) => {
                const done = d.status === "Delivered" ? true : i === 0;
                return (
                  <React.Fragment key={step}>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                      <CheckCircle2 className="h-3 w-3" /> {step}
                    </span>
                    {i < 2 && <span className="h-px w-6 bg-slate-200" />}
                  </React.Fragment>
                );
              })}
            </div>
            {del && (
              <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-2">
                Delivered {fmtNum(del.actual_quantity)} gal · Variance {fmtNum(del.variance)} gal · Received by {del.recipient} · {fmtDate(del.delivered_at)}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function Profile({ summary }) {
  const c = summary?.customer || {};
  const rows = [["Customer Code", c.code], ["Legal Name", c.name], ["Type", c.type], ["Email", c.email],
    ["Phone", c.phone], ["Tax ID", c.tax_id], ["Payment Terms", c.payment_terms],
    ["Credit Limit", fmtMoney(c.credit_limit)], ["Risk Rating", c.risk_rating], ["Status", c.status]];
  return (
    <Card className="p-6 max-w-xl">
      <h3 className="font-head font-semibold text-slate-800 mb-4">Company Profile</h3>
      <div className="divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between py-2 text-sm">
            <span className="text-slate-500">{k}</span>
            <span className="font-medium text-slate-800">{v ?? "—"}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-4">To update company details, please contact your PCI account manager.</p>
    </Card>
  );
}

export default function Portal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const reload = useCallback(() => api.get("/portal/summary").then(({ data }) => setSummary(data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-[#12346B] text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center overflow-hidden"><img src="/prime-logo.jpg" alt="PCI" className="h-9 w-9 object-cover" /></div>
          <div className="leading-tight">
            <div className="font-head font-extrabold text-sm">PCI PetroFlow · Customer Portal</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Prime Contractors, Inc.</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-slate-200 hidden sm:block">{user?.name}</span>
            <button onClick={logout} data-testid="portal-logout" className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center" title="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] border-b-2 whitespace-nowrap transition-colors ${
                tab === t.key ? "border-orange-500 text-white font-semibold" : "border-transparent text-slate-300 hover:text-white"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-6">
        <h1 className="font-head text-xl font-bold text-slate-900 mb-4">{TABS.find((t) => t.key === tab)?.label}</h1>
        {tab === "overview" && <Overview summary={summary} go={setTab} />}
        {tab === "order" && <PlaceOrder reload={reload} go={setTab} />}
        {tab === "orders" && <MyOrders go={setTab} />}
        {tab === "payments" && <Payments />}
        {tab === "deliveries" && <Deliveries />}
        {tab === "profile" && <Profile summary={summary} />}
      </main>
      <footer className="text-center text-[11px] text-slate-400 py-3">PCI PetroFlow ERP™ · Secure Customer Portal · Prime Contractors, Inc.</footer>
    </div>
  );
}
